import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Camera, X, Loader2 } from "lucide-react";
import { StarRating } from "@/components/StarRating";
import { useCustomerAuth, startGoogleLogin } from "@/context/CustomerAuthContext";

export default function WriteReviewDialog({ open, onOpenChange, productId, productName, onSubmitted }) {
  const { user: customer } = useCustomerAuth();
  const [eligibility, setEligibility] = useState(null);
  const [checking, setChecking] = useState(false);
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [images, setImages] = useState([]);  // list of urls
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!customer) { setEligibility({ can_review: false, reason: "signin" }); return; }
    setChecking(true);
    const params = productId ? `?product_id=${productId}` : "";
    api.get(`/reviews/eligibility${params}`)
      .then(({ data }) => setEligibility(data))
      .catch(() => setEligibility({ can_review: false, verified_purchase: false }))
      .finally(() => setChecking(false));
  }, [open, customer, productId]);

  const reset = () => {
    setRating(0); setTitle(""); setBody(""); setImages([]);
  };

  const uploadImage = async (file) => {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post("/uploads/review-photo", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data.url;
  };

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const remaining = 3 - images.length;
    if (remaining <= 0) { toast.error("Max 3 photos"); return; }
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files.slice(0, remaining)) {
        try {
          const url = await uploadImage(f);
          uploaded.push(url);
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Upload failed");
        }
      }
      setImages((prev) => [...prev, ...uploaded]);
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!rating) { toast.error("Pick a rating first"); return; }
    if (body.trim().length < 5) { toast.error("Add a few words to your review"); return; }
    setSubmitting(true);
    try {
      await api.post("/reviews", {
        product_id: productId || null,
        rating,
        title: title.trim() || null,
        body: body.trim(),
        image_urls: images,
      });
      toast.success("Thanks — your review is live!");
      reset();
      onSubmitted?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't post review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="write-review-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-light">Write a review</DialogTitle>
          {productName && <p className="text-xs uppercase tracking-[0.2em] text-stone-500 mt-1">{productName}</p>}
        </DialogHeader>

        {!customer && (
          <div className="py-6 text-center space-y-3">
            <p className="text-sm text-stone-600">Sign in with the email you checked out with to leave a review.</p>
            <Button onClick={() => { onOpenChange(false); startGoogleLogin(); }} className="rounded-full bg-[#1A1C1A] hover:bg-[#3C3E3C]">
              Sign in
            </Button>
          </div>
        )}

        {customer && checking && <div className="py-8 text-center text-stone-500 text-sm">Checking eligibility…</div>}

        {customer && !checking && eligibility && !eligibility.can_review && (
          <div className="py-6 text-center space-y-2">
            {eligibility.already_reviewed ? (
              <p className="text-sm text-stone-600">You've already reviewed this. Reach out if you'd like to update it.</p>
            ) : !eligibility.verified_purchase ? (
              <>
                <p className="text-sm text-stone-600">Only customers who've purchased can review. Make sure you're signed in with the same email you used at checkout.</p>
                <Link to="/shop" onClick={() => onOpenChange(false)} className="text-xs uppercase tracking-[0.2em] text-[#1A1C1A] hover:underline inline-block mt-2">Shop the collection →</Link>
              </>
            ) : (
              <p className="text-sm text-stone-600">You're not able to review right now.</p>
            )}
          </div>
        )}

        {customer && !checking && eligibility?.can_review && (
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Your rating</Label>
              <div className="mt-2"><StarRating value={rating} onChange={setRating} size={28} /></div>
            </div>
            <div>
              <Label className="text-xs">Title (optional)</Label>
              <Input data-testid="review-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Best gym set I own" maxLength={140} />
            </div>
            <div>
              <Label className="text-xs">Your review</Label>
              <Textarea data-testid="review-body" rows={5} value={body} onChange={(e) => setBody(e.target.value)} placeholder="What did you love? How did it fit? Anything the next customer should know?" />
            </div>
            <div>
              <Label className="text-xs">Photos (up to 3, optional)</Label>
              <div className="mt-2 flex items-center gap-3 flex-wrap">
                {images.map((u, i) => (
                  <div key={i} className="relative">
                    <img src={u.startsWith("http") ? u : `${(process.env.REACT_APP_BACKEND_URL || "")}${u}`} alt="" className="w-16 h-16 object-cover rounded-md border border-[#E5E2DC]" />
                    <button type="button" onClick={() => setImages(images.filter((_, idx) => idx !== i))} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#1A1C1A] text-white flex items-center justify-center">
                      <X className="w-3 h-3" strokeWidth={2} />
                    </button>
                  </div>
                ))}
                {images.length < 3 && (
                  <label className="w-16 h-16 border border-dashed border-stone-300 rounded-md flex items-center justify-center cursor-pointer hover:border-[#1A1C1A] transition-colors" data-testid="review-photo-picker">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin text-stone-400" /> : <Camera className="w-5 h-5 text-stone-400" strokeWidth={1.5} />}
                    <input type="file" accept="image/*" multiple className="hidden" onChange={onPickFiles} disabled={uploading} />
                  </label>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} className="rounded-full">Cancel</Button>
              <Button data-testid="review-submit" onClick={submit} disabled={submitting} className="rounded-full bg-[#1A1C1A] hover:bg-[#3C3E3C]">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Posting…</> : "Post review"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
